/**
 * Authored-track playlist with a procedural-music fallback.
 *
 * Add future songs to DEFAULT_TRACKS in playback order. Once every authored track has
 * ended, the existing adaptive MusicEngine takes over for the rest of the game session.
 * Stopping or resetting a session rewinds the playlist to its first song.
 */

import { MusicEngine } from './music.js';

const DEFAULT_TRACKS = Object.freeze([
  Object.freeze({
    id: 'nid-de-poule',
    src: new URL('./tracks/nid-de-poule.mp3', import.meta.url).href,
    gain: 1,
  }),
]);

function defaultCreateAudio() {
  return typeof Audio === 'undefined' ? null : new Audio();
}

export class Soundtrack {
  constructor(graph, randTable, opts = {}) {
    this.graph = graph;
    this.generated = opts.generated || new MusicEngine(graph, randTable);
    this.tracks = opts.tracks || DEFAULT_TRACKS;
    this.createAudio = opts.createAudio || defaultCreateAudio;

    this.built = false;
    this.playing = false;
    this.active = 'none';
    this.trackIndex = 0;
    this.fallbackReason = null;
    this.playAttempt = 0;

    this.element = null;
    this.mediaSource = null;
    this.trackGain = null;
    this._onEnded = () => this._advanceTrack();
    this._onError = () => this._advanceTrack('media-error');
  }

  build() {
    this.generated.build();
    if (this.built || !this.graph.live) return;
    this.built = true;

    try {
      const element = this.createAudio();
      const a = this.graph.actx;
      if (!element || !a || typeof a.createMediaElementSource !== 'function') {
        this.fallbackReason = 'media-element-unavailable';
      } else {
        element.preload = 'auto';
        element.loop = false;
        element.addEventListener('ended', this._onEnded);
        element.addEventListener('error', this._onError);

        this.trackGain = a.createGain();
        this.trackGain.gain.value = 1;
        this.mediaSource = a.createMediaElementSource(element);
        this.mediaSource.connect(this.trackGain);
        this.trackGain.connect(this.graph.bus.music);
        this.element = element;
      }
    } catch (error) {
      this.fallbackReason = error && error.name ? error.name : 'media-element-build-failed';
      this.element = null;
    }

    // start() can run before WebAudio exists. Replace its model-only procedural playback
    // with the real authored track as soon as the graph comes alive.
    if (this.playing) {
      this.generated.stop();
      this.active = 'none';
      this._playCurrentOrGenerated();
    }
  }

  command(cmd) {
    if (typeof cmd !== 'string') return;
    if (cmd === 'start') this.start();
    else if (cmd === 'stop' || cmd === 'pause') this.stop();
    else this.generated.command(cmd);
  }

  start() {
    if (!this.playing) this.playing = true;
    if (this.active === 'file' || this.active === 'generated') return;

    if (this.graph.live && this.built) {
      this._playCurrentOrGenerated();
    } else {
      // Preserve the deterministic, no-WebAudio harness model until build() can start the
      // actual playlist. The generated engine remains the model used by existing stats.
      this.active = 'generated';
      this.generated.start();
    }
  }

  stop() {
    this.playing = false;
    this.playAttempt++;
    if (this.element) {
      this.element.pause();
      try { this.element.currentTime = 0; } catch { /* metadata may not be loaded yet */ }
    }
    this.generated.stop();
    this.trackIndex = 0;
    this.active = 'none';
  }

  setAuto(combo, timeRemaining, speedKmh) {
    this.generated.setAuto(combo, timeRemaining, speedKmh);
  }

  update(dt) {
    this.generated.update(dt);
  }

  reset() {
    this.stop();
    this.generated.reset();
    this.fallbackReason = null;
  }

  get clock() { return this.generated.clock; }
  set clock(value) { this.generated.clock = value; }

  stats() {
    const generated = this.generated.stats();
    const track = this.trackIndex < this.tracks.length ? this.tracks[this.trackIndex] : null;
    return {
      ...generated,
      playing: this.playing,
      generatedPlaying: !!generated.playing,
      source: this.active,
      trackId: this.active === 'file' && track ? track.id : null,
      trackIndex: this.trackIndex,
      trackCount: this.tracks.length,
      fallbackReason: this.fallbackReason,
    };
  }

  dispose() {
    this.playAttempt++;
    if (this.element) {
      this.element.pause();
      this.element.removeEventListener('ended', this._onEnded);
      this.element.removeEventListener('error', this._onError);
      this.element.removeAttribute('src');
      this.element.load();
    }
    if (this.mediaSource) this.mediaSource.disconnect();
    if (this.trackGain) this.trackGain.disconnect();
    this.element = null;
    this.mediaSource = null;
    this.trackGain = null;
    this.playing = false;
    this.active = 'none';
  }

  _playCurrentOrGenerated() {
    if (!this.playing) return;
    if (!this.element || this.trackIndex >= this.tracks.length) {
      this.active = 'generated';
      this.generated.start();
      return;
    }

    const track = this.tracks[this.trackIndex];
    this.generated.stop();
    this.active = 'file';
    this.trackGain.gain.value = track.gain == null ? 1 : track.gain;
    this.element.src = track.src;
    try { this.element.currentTime = 0; } catch { /* metadata may not be loaded yet */ }
    this.element.load();

    const attempt = ++this.playAttempt;
    const result = this.element.play();
    if (result && typeof result.catch === 'function') {
      result.catch((error) => {
        if (!this.playing || this.active !== 'file' || attempt !== this.playAttempt) return;
        this._advanceTrack(error && error.name ? error.name : 'play-rejected');
      });
    }
  }

  _advanceTrack(reason = null) {
    if (!this.playing || this.active !== 'file') return;
    this.playAttempt++;
    if (reason) this.fallbackReason = reason;
    this.trackIndex++;
    this.active = 'none';
    this._playCurrentOrGenerated();
  }
}
